import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, message } from "antd";
import FacilitiesFilter from "./FacilitiesFilter";
import FacilitiesTable from "./FacilitiesTable";
import AddFacilityModal from "./AddFacilityModal";
import { httpService } from "../../../../services/httpService.service";
import "../VisitBoard/VisitBoard.scss";
import "./facilities.scss";

const DEBOUNCE_DELAY = 350; // ms

const Facilities = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [allFacilities, setAllFacilities] = useState([]); // Store all facilities
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ Search: "" });
  // What the debounce has actually committed — the filtered list is derived
  // from it, so a refetch (after an add or a delete) keeps the active search
  // instead of silently showing everything again.
  const [appliedSearch, setAppliedSearch] = useState("");
  const [pagination, setPagination] = useState({ pageSize: 10, current: 1 });
  const [editingFacility, setEditingFacility] = useState(null);
  const debounceRef = useRef();

  useEffect(() => {
    fetchFacilities();
    return () => clearTimeout(debounceRef.current);
  }, []);

  const fetchFacilities = () => {
    setLoading(true);
    httpService.get(
      "/api/Facility",
      (res) => {
        setAllFacilities(res.data || []);
        setLoading(false);
      },
      (error) => {
        message.error("Failed to fetch facilities");
        setLoading(false);
      }
    );
  };

  // The whole filtered list goes to the Table, which slices it for the current
  // page. It used to be handed a hard-coded total of 0, which made antd hide
  // the pager altogether, and onPageChange was a no-op.
  const facilities = useMemo(() => {
    if (!appliedSearch) return allFacilities;
    return allFacilities.filter((facility) =>
      (facility.name || "").toLowerCase().includes(appliedSearch)
    );
  }, [allFacilities, appliedSearch]);

  // Deleting the last row of the last page (or narrowing a search) can leave
  // the pager pointing past the end — walk it back rather than showing blank.
  useEffect(() => {
    const lastPage = Math.max(
      1,
      Math.ceil(facilities.length / pagination.pageSize)
    );
    if (pagination.current > lastPage) {
      setPagination((prev) => ({ ...prev, current: lastPage }));
    }
  }, [facilities.length, pagination.current, pagination.pageSize]);

  const handleFiltersChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      setAppliedSearch((newFilters.Search || "").trim().toLowerCase());
      setPagination((prev) => ({ ...prev, current: 1 }));
      setLoading(false);
    }, DEBOUNCE_DELAY);
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleAddFacility = (values) => {
    setLoading(true);
    if (editingFacility) {
      httpService.put(
        `/api/Facility/${editingFacility.id}`,
        { name: values.name, color: values.color },
        (res) => {
          setIsModalVisible(false);
          setEditingFacility(null);
          fetchFacilities();
        },
        (error) => {
          message.error("Failed to update facility");
          setLoading(false);
        }
      );
    } else {
      httpService.post(
        "/api/Facility",
        values,
        (res) => {
          setIsModalVisible(false);
          fetchFacilities();
        },
        (error) => {
          message.error("Failed to add facility");
          setLoading(false);
        }
      );
    }
  };

  const handleDelete = (record) => {
    setLoading(true);
    httpService.delete(
      `/api/Facility/${record.id}`,
      null,
      (res) => {
        message.success("Facility deleted successfully");
        fetchFacilities();
      },
      (error) => {
        message.error("Failed to delete facility");
        setLoading(false);
      }
    );
  };

  const handleEdit = (record) => {
    setEditingFacility(record);
    setIsModalVisible(true);
  };
  const handlePreview = (record) => {
    // TODO: Show preview modal
  };
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingFacility(null);
  };

  return (
    <div className="ip-planning-wrapper">
      <div className="tableViewHeader d-flex justify-content-between align-items-center mb-5">
        <span className="tableViewHeaderText">Facilities</span>
        <Button
          className="addButtonTPM"
          type="primary"
          onClick={() => {
            setEditingFacility(null);
            setIsModalVisible(true);
          }}
        >
          + Add Facility
        </Button>
      </div>
      <FacilitiesFilter
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      <FacilitiesTable
        data={facilities}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: facilities.length,
          showSizeChanger: true,
          showTotal: (total) => `${total} facilities`,
        }}
        onPageChange={handleTableChange}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPreview={handlePreview}
      />
      <AddFacilityModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={handleAddFacility}
        facility={editingFacility}
      />
    </div>
  );
};

export default Facilities;
